/**
 * Source Colors Utility
 * 
 * Maps listing source (Airbnb, VRBO, Website/Direct, Owners) to colors.
 * Currently uses channel as a proxy for source, but structured to support
 * a dedicated source field in the future.
 * 
 * Color scheme (single source of truth: CSS tokens):
 * - VRBO: #4A6FAE (matches --color-channel-vrbo)
 * - Airbnb: #F2BFC0 (matches --color-channel-airbnb)
 * - Website/Direct/CLC: #C7E0F4 (matches --color-channel-clc)
 * - Owners: #D8CFC4 (matches --color-channel-owner)
 * - Fallback: #6B7280 (neutral gray)
 */

/**
 * Normalize source/channel value to standard source identifier
 * @param {string} sourceOrChannel - Source or channel code
 * @returns {string} Normalized source identifier
 */
function normalizeSource(sourceOrChannel) {
  if (!sourceOrChannel) return null;
  
  const normalized = String(sourceOrChannel).toLowerCase().trim();
  
  // Map channel codes to source identifiers
  // Airbnb channels → airbnb source
  if (normalized === 'airbnb' || normalized.includes('airbnb')) {
    return 'airbnb';
  }
  
  // VRBO channels → vrbo source
  if (normalized === 'vrbo' || normalized.includes('vrbo')) {
    return 'vrbo';
  }
  
  // Booking.com channels → booking source (treat as website for now)
  if (normalized === 'booking.com' || normalized === 'booking' || normalized.includes('booking')) {
    return 'website';
  }
  
  // Direct/CLC channels → website source
  if (normalized === 'direct' || normalized === 'clc' || normalized === 'website') {
    return 'website';
  }
  
  // Owner channels → owners source
  if (normalized === 'owner' || normalized === 'owners') {
    return 'owners';
  }
  
  // Unknown → null (will use fallback)
  return null;
}

/**
 * Get human-readable label for source
 * @param {string} sourceOrChannel - Source or channel code
 * @returns {string} Human-readable label
 */
export function getSourceLabel(sourceOrChannel) {
  const source = normalizeSource(sourceOrChannel);
  
  const labelMap = {
    'airbnb': 'Airbnb',
    'vrbo': 'VRBO',
    'website': 'Website',
    'owners': 'Owners',
  };
  
  return labelMap[source] || 'Direct';
}

/**
 * Get hex color for source
 * Uses CSS tokens as single source of truth
 * @param {string} sourceOrChannel - Source or channel code
 * @returns {string} Hex color code
 */
export function getSourceColorHex(sourceOrChannel) {
  const source = normalizeSource(sourceOrChannel);
  
  // Single source of truth: CSS tokens
  // These values match --color-channel-* tokens in design-tokens.css and tokens/clc.css
  const colorMap = {
    'airbnb': '#F2BFC0',      // Airbnb (matches --color-channel-airbnb)
    'vrbo': '#4A6FAE',       // VRBO (matches --color-channel-vrbo)
    'website': '#C7E0F4',    // CLC (matches --color-channel-clc)
    'owners': '#D8CFC4',     // Owner (matches --color-channel-owner)
  };
  
  return colorMap[source] || '#6B7280'; // Neutral gray fallback
}

/**
 * Get RGBA color for source with specified alpha
 * @param {string} sourceOrChannel - Source or channel code
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string
 */
export function getSourceColorRgba(sourceOrChannel, alpha = 1) {
  const hex = getSourceColorHex(sourceOrChannel);
  
  // Convert hex to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Get source and channel display string
 * Format: "Source" only (e.g., "Airbnb")
 * Removes raw channel codes like "airbnbOfficial" and shows only friendly source label
 * @param {string} sourceOrChannel - Source or channel code
 * @param {string} channelName - Optional channel name (ignored - we only show source label)
 * @returns {string} Formatted display string (source label only)
 */
export function getSourceChannelDisplay(sourceOrChannel, channelName = null) {
  // Always return only the source label (e.g., "Airbnb", "VRBO", "Direct")
  // Do not show raw channel codes like "airbnbOfficial" or "homeaway"
  const sourceLabel = getSourceLabel(sourceOrChannel);
  return sourceLabel;
}
